<template>
  <div class="py-4 container-fluid">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-lg-12 position-relative z-index-2">
        <div class="row">
          <div class="col-sm-7">
            <h1 class="mainTitle">{{ $t('dashboard') }}</h1>
            <span class="mainDescription">{{ $t('helloAdmin') }}</span>
          </div>
          <div class="col-sm-5 d-flex justify-content-sm-end align-items-center mt-3 mt-sm-0">
            <div class="report-navigation d-flex flex-wrap gap-2 justify-content-sm-end">
              <button
                type="button"
                class="btn btn-sm btn-outline-success mb-0 d-inline-flex align-items-center dashboard-export-button"
                :disabled="exportingOrders"
                @click="exportAllOrders"
              >
                <i class="material-icons-round me-1 align-middle text-sm">{{ exportingOrders ? 'hourglass_top' : 'file_download' }}</i>
                {{ exportingOrders ? $t('exporting') : $t('exportOrders') }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-success mb-0 d-inline-flex align-items-center dashboard-export-button"
                :disabled="exportingDateRangeOrders"
                @click="showDateRangeExportModal = true"
              >
                <i class="material-icons-round me-1 align-middle text-sm">event</i>
                {{ $t('exportDateRange') }}
              </button>
              <button type="button" class="btn btn-sm btn-outline-warning mb-0" @click="openDateRangeOrderReport">
                <i class="material-icons-round me-1 align-middle text-sm">date_range</i> {{ $t('orderReport') }}
              </button>
              <router-link :to="{ name: 'MonthlyOrderReport' }" class="btn btn-sm text-white mb-0" style="background-color: #CBA048">
                <i class="material-icons-round me-1 align-middle text-sm">assessment</i> {{ $t('monthlyOrderReport') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDateRangeExportModal" class="modal-backdrop fade show"></div>
    <div v-if="showDateRangeExportModal" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true" aria-labelledby="date-range-export-title">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <form class="modal-content" @submit.prevent="exportOrdersByDateRange">
          <div class="modal-header">
            <div>
              <h5 id="date-range-export-title" class="modal-title">{{ $t('exportOrdersByDate') }}</h5>
              <p class="text-sm text-secondary mb-0">{{ $t('selectedDatesIncluded') }}</p>
            </div>
            <button type="button" class="btn-close" aria-label="Close" :disabled="exportingDateRangeOrders" @click="closeDateRangeExportModal"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-sm-6">
                <label for="export-date-from" class="form-label">{{ $t('from') }}</label>
                <input id="export-date-from" v-model="exportDateFrom" type="date" class="form-control" required @click="openDatePicker">
              </div>
              <div class="col-sm-6">
                <label for="export-date-to" class="form-label">{{ $t('to') }}</label>
                <input id="export-date-to" v-model="exportDateTo" type="date" class="form-control" required :min="exportDateFrom || undefined" @click="openDatePicker">
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary mb-0" :disabled="exportingDateRangeOrders" @click="closeDateRangeExportModal">{{ $t('cancel') }}</button>
            <button type="submit" class="btn btn-success mb-0" :disabled="exportingDateRangeOrders">
              <i class="material-icons-round me-1 align-middle text-sm">file_download</i>
              {{ exportingDateRangeOrders ? $t('exporting') : $t('exportCsv') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="row">
      <div class="col-xl-3 col-sm-6 mb-4" v-for="card in stats" :key="card.title">
        <div class="card shadow border-0" :style="{ backgroundColor: '#CBA048', color: '#fff' }">
          <div class="card-body text-center py-4">
            <div class="mb-2">
              <i class="material-icons-round" style="font-size: 2rem; color: white">{{ card.icon }}</i>
            </div>
            <h6 class="text-white text-sm mb-1">{{ $t(card.titleKey) }}</h6>
            <h4 class="mb-0">{{ card.value }}</h4>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-12 mb-3">
        <h5 class="mb-0">{{ $t('payouts') }}</h5>
        <p class="text-sm text-secondary mb-0">{{ $t('payoutsDesc') }}</p>
      </div>
      <div class="col-xl-3 col-sm-6 mb-4" v-for="card in payoutSummaryCards" :key="card.label">
        <div class="card shadow border-0">
          <div class="card-body d-flex align-items-center gap-3 py-3">
            <div class="summary-icon" :class="card.colorClass">
              <i class="material-icons-round">{{ card.icon }}</i>
            </div>
            <div>
              <p class="text-sm text-secondary mb-0">{{ card.label }}</p>
              <h5 class="mb-0">{{ card.value }}</h5>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-12 mb-3">
        <h5 class="mb-0">{{ $t('totalOrders') }}</h5>
        <p class="text-sm text-secondary mb-0">{{ $t('allTimeOrderActivity') }}</p>
      </div>
      <div class="col-xl-3 col-sm-6 mb-4" v-for="card in totalOrderSummaryCards" :key="card.label">
        <div class="card shadow border-0">
          <div class="card-body d-flex align-items-center gap-3 py-3">
            <div class="summary-icon" :class="card.colorClass">
              <i class="material-icons-round">{{ card.icon }}</i>
            </div>
            <div>
              <p class="text-sm text-secondary mb-0">{{ card.label }}</p>
              <h5 class="mb-0">{{ card.value }}</h5>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-xl-3 col-sm-6 mb-4" v-for="card in totalOrderStatusCards" :key="`total-${card.status}`">
        <div class="card shadow border-0">
          <div class="card-body d-flex align-items-center gap-3 py-3">
            <div class="status-icon" :class="card.colorClass">
              <i class="material-icons-round">{{ card.icon }}</i>
            </div>
            <div>
              <p class="text-sm text-secondary mb-0">{{ card.label }}</p>
              <h5 class="mb-0">{{ card.value }}</h5>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-12 mb-3">
        <h5 class="mb-0">{{ $t('ordersThisMonth') }}</h5>
      </div>
      <div class="col-xl-3 col-sm-6 mb-4" v-for="card in orderStatusCards" :key="card.status">
        <div class="card shadow border-0">
          <div class="card-body d-flex align-items-center gap-3 py-3">
            <div class="status-icon" :class="card.colorClass">
              <i class="material-icons-round">{{ card.icon }}</i>
            </div>
            <div>
              <p class="text-sm text-secondary mb-0">{{ card.label }}</p>
              <h5 class="mb-0">{{ card.value }}</h5>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-xl-7 mb-4">
        <div class="card shadow border-0 h-100">
          <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center pt-4 px-4">
            <div>
              <h6 class="mb-1">{{ $t('todaysPerformance') }}</h6>
              <p class="text-sm text-secondary mb-0">{{ $t('todaysOrderActivity') }}</p>
            </div>
            <i class="material-icons-round text-warning">insights</i>
          </div>
          <div class="card-body px-4 pb-4">
            <div class="row g-3">
              <div class="col-sm-6" v-for="metric in todayPerformanceCards" :key="metric.label">
                <div class="performance-card">
                  <div class="performance-card__icon">
                    <i class="material-icons-round">{{ metric.icon }}</i>
                  </div>
                  <div>
                    <p class="text-sm text-secondary mb-1">{{ metric.label }}</p>
                    <h5 class="mb-0">{{ metric.value }}</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-5 mb-4">
        <div class="card shadow border-0 h-100">
          <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center pt-4 px-4">
            <div>
              <h6 class="mb-1">{{ $t('lowStockAlerts') }}</h6>
              <p class="text-sm text-secondary mb-0">{{ $t('itemsWithUnitsOrFewer', { count: lowStock.threshold }) }}</p>
            </div>
            <span class="low-stock-count">{{ lowStock.count }}</span>
          </div>
          <div class="card-body px-4 pb-3">
            <div v-if="lowStock.items.length" class="low-stock-list">
              <router-link
                v-for="item in lowStock.items"
                :key="`${item.product_id}-${item.variant_sku || item.sku || item.name}`"
                :to="{ name: 'ProductEdit', params: { id: item.product_id } }"
                class="low-stock-item"
              >
                <img
                  v-if="item.image_url"
                  :src="item.image_url"
                  :alt="item.name"
                  class="low-stock-item__image"
                >
                <span v-else class="low-stock-item__image low-stock-item__image--placeholder">
                  <i class="material-icons-round">inventory_2</i>
                </span>
                <div class="low-stock-item__details">
                  <strong>{{ item.name }}</strong>
                  <span>{{ lowStockItemDetails(item) }}</span>
                </div>
                <span class="low-stock-item__quantity">{{ item.quantity }} {{ $t('left') }}</span>
              </router-link>
            </div>
            <div v-else class="empty-stock-state">
              <i class="material-icons-round">inventory_2</i>
              <span>{{ $t('allProductsAboveAlert') }}</span>
            </div>
          </div>
          <div class="card-footer bg-white border-0 px-4 pb-4 pt-0">
            <router-link :to="{ name: 'Products' }" class="text-warning text-sm fw-bold">
              {{ $t('viewInventory') }} <i class="material-icons-round align-middle text-sm">arrow_forward</i>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Links -->
    <div class="row mt-4">
      <div class="col-md-12">
        <h5 class="mb-3">{{ $t('quickActions') }}</h5>
        <div class="d-flex gap-3 flex-wrap">
          <router-link to="/products/create" class="btn text-white" style="background-color: #CBA048">
            <i class="material-icons-round me-1">add</i> {{ $t('newProduct') }}
          </router-link>
          <router-link to="/contacts" class="btn text-white" style="background-color: #CBA048">
            <i class="material-icons-round me-1">mail</i> {{ $t('viewMessages') }}
          </router-link>
        </div>
      </div>
    </div>


    <!-- Revenue Chart (Hidden)
    <div class="row mt-4">
      <div class="col-md-12">
        <h5 class="mb-3">Revenue (Last 6 Months)</h5>
        <canvas id="revenueChart"></canvas>
      </div>
    </div>
    -->
  </div>
</template>

<script>
import axios from "@/Axios";
import OrderService from "@/services/order.service";
import showSwal from "@/mixins/showSwal";
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
  name: "dashboard-default",
  mixins: [showSwal],
  data() {
    return {
      exportingOrders: false,
      exportingDateRangeOrders: false,
      showDateRangeExportModal: false,
      exportDateFrom: '',
      exportDateTo: '',
      stats: [
        { titleKey: 'contacts', value: 0, icon: "contacts", key: "contacts" },
        { titleKey: 'products', value: 0, icon: "inventory_2", key: "products" },
      ],
      overview: {
        monthly_revenue: {},
      },
      totalOrderReport: {
        summary: {},
        order_status_counts: {},
      },
      payouts: {
        settled_amount: 0,
        pending_amount: 0,
      },
      orderStatusCounts: {},
      todayPerformance: {},
      lowStock: {
        threshold: 5,
        count: 0,
        items: [],
      },
      orderStatusDefinitions: [
        { status: 'pending', labelKey: 'pending', icon: 'schedule', colorClass: 'status-icon--warning' },
        { status: 'processing', labelKey: 'processing', icon: 'sync', colorClass: 'status-icon--info' },
        { status: 'approved_customer', labelKey: 'approvedCustomer', icon: 'task_alt', colorClass: 'status-icon--primary' },
        { status: 'approved_stock', labelKey: 'approvedStock', icon: 'inventory_2', colorClass: 'status-icon--primary' },
        { status: 'out_for_delivery', labelKey: 'outForDelivery', icon: 'local_shipping', colorClass: 'status-icon--primary' },
        { status: 'out_for_delivery_courier', labelKey: 'outForDeliveryCourier', icon: 'local_shipping', colorClass: 'status-icon--primary' },
        { status: 'shipped', labelKey: 'shipped', icon: 'local_shipping', colorClass: 'status-icon--primary' },
        { status: 'delivered', labelKey: 'delivered', icon: 'check_circle', colorClass: 'status-icon--success' },
        { status: 'cancelled', labelKey: 'cancelled', icon: 'cancel', colorClass: 'status-icon--danger' },
        { status: 'returned', labelKey: 'returned', icon: 'undo', colorClass: 'status-icon--secondary' },
      ],
    };
  },

  computed: {
    totalOrderSummaryCards() {
      const summary = this.totalOrderReport.summary || {};

      return [
        { label: this.$t('totalOrders'), value: this.formatNumber(summary.total_orders), icon: 'shopping_bag', colorClass: 'summary-icon--gold' },
        { label: this.$t('orderValue'), value: this.formatAmount(summary.order_value), icon: 'payments', colorClass: 'summary-icon--blue' },
        { label: this.$t('deliveredRevenue'), value: this.formatAmount(summary.delivered_revenue), icon: 'check_circle', colorClass: 'summary-icon--green' },
        { label: this.$t('averageOrderValue'), value: this.formatAmount(summary.average_order_value), icon: 'analytics', colorClass: 'summary-icon--purple' },
      ];
    },
    payoutSummaryCards() {
      return [
        { label: this.$t('settledAmount'), value: this.formatAmount(this.payouts.settled_amount), icon: 'account_balance_wallet', colorClass: 'summary-icon--green' },
        { label: this.$t('pendingPayout'), value: this.formatAmount(this.payouts.pending_amount), icon: 'hourglass_top', colorClass: 'summary-icon--gold' },
      ];
    },
    totalOrderStatusCards() {
      const counts = this.totalOrderReport.order_status_counts || {};

      return this.orderStatusDefinitions.map((definition) => ({
        ...definition,
        label: this.$t(definition.labelKey),
        value: counts[definition.status] || 0,
      }));
    },
    orderStatusCards() {
      return this.orderStatusDefinitions.map((definition) => ({
        ...definition,
        label: this.$t(definition.labelKey),
        value: this.orderStatusCounts[definition.status] || 0,
      }));
    },
    todayPerformanceCards() {
      return [
        { label: this.$t('ordersPlaced'), value: this.formatNumber(this.todayPerformance.orders), icon: 'shopping_bag' },
        { label: this.$t('orderValue'), value: this.formatNumber(this.todayPerformance.order_value), icon: 'payments' },
        { label: this.$t('averageOrderValue'), value: this.formatNumber(this.todayPerformance.average_order_value), icon: 'analytics' },
        { label: this.$t('delivered'), value: this.formatNumber(this.todayPerformance.delivered), icon: 'local_shipping' },
      ];
    },
  },

  async created() {
    try {
      const [statsRes, totalOrderReportRes, orderReportRes] = await Promise.all([
        axios.get("/dashboard/stats"),
        axios.get("/dashboard/total-order-report"),
        axios.get("/dashboard/order-report"),
      ]);
      const statsData = statsRes.data.data;
      const totalOrderReportData = totalOrderReportRes.data.data;
      const orderReportData = orderReportRes.data.data;

      this.stats = this.stats.map((stat) => ({
        ...stat,
        value: statsData[stat.key] || 0,
      }));

      this.overview.monthly_revenue = statsData.monthly_revenue || {};
      this.totalOrderReport = { ...this.totalOrderReport, ...totalOrderReportData };
      this.payouts = { ...this.payouts, ...(statsData.payouts || {}) };
      this.orderStatusCounts = orderReportData.order_status_counts || {};
      this.todayPerformance = statsData.today_performance || {};
      this.lowStock = { ...this.lowStock, ...(statsData.low_stock || {}) };

      this.$nextTick(() => this.renderChart());
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    }
  },

  methods: {
    lowStockItemDetails(item) {
      const size = this.getLowStockSize(item);
      const sku = item.variant_sku || item.sku;
      const details = [];

      if (size) details.push(`${this.$t('size')}: ${size}`);
      if (sku) details.push(sku);

      return details.join(' · ') || this.$t('noSku');
    },

    getLowStockSize(item) {
      if (item.size) return item.size;
      if (item.size_value) return item.size_value;

      const optionValues = item.option_values || item.variant_option_values || [];
      const sizeOption = optionValues.find((option) => {
        const typeName = option.option_type_name
          || option.option_type?.name
          || option.product_option?.option_type?.name
          || '';

        return typeName.toLowerCase() === 'size';
      });

      return sizeOption?.value_en || sizeOption?.value || item.variant_name || item.variant_title || '';
    },

    async exportAllOrders() {
      if (this.exportingOrders) return;
      this.exportingOrders = true;

      try {
        const response = await OrderService.exportOrders();

        this.downloadCsv(response, `orders-${new Date().toISOString().slice(0, 10)}.csv`);

        this.showSuccessMessage(this.$t('ordersExported'));
      } catch (error) {
        console.error('Failed to export orders:', error);
        this.showErrorMessage(this.$t('ordersExportFailed'));
      } finally {
        this.exportingOrders = false;
      }
    },

    closeDateRangeExportModal() {
      if (this.exportingDateRangeOrders) return;

      this.showDateRangeExportModal = false;
    },

    openDatePicker(event) {
      try {
        event.currentTarget.showPicker?.();
      } catch (error) {
        // Browsers without showPicker still open their native calendar normally.
      }
    },

    async exportOrdersByDateRange() {
      if (this.exportingDateRangeOrders) return;

      if (!this.exportDateFrom || !this.exportDateTo) {
        this.showErrorMessage(this.$t('selectBothDates'));
        return;
      }

      if (this.exportDateFrom > this.exportDateTo) {
        this.showErrorMessage(this.$t('invalidDateRange'));
        return;
      }

      this.exportingDateRangeOrders = true;

      try {
        const response = await OrderService.exportOrdersByDateRange(this.exportDateFrom, this.exportDateTo);
        this.downloadCsv(response, `orders-${this.exportDateFrom}-to-${this.exportDateTo}.csv`);
        this.showSuccessMessage(this.$t('ordersExported'));
        this.showDateRangeExportModal = false;
      } catch (error) {
        console.error('Failed to export orders by date range:', error);
        this.showErrorMessage(this.$t('ordersExportFailed'));
      } finally {
        this.exportingDateRangeOrders = false;
      }
    },

    downloadCsv(response, fallbackFilename) {
      let filename = fallbackFilename;
      const disposition = response.headers?.['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    },

    openDateRangeOrderReport() {
      this.$router.push({ name: 'OrderReport' });
    },

    formatNumber(value) {
      return new Intl.NumberFormat(this.$store.state.locale === 'ar' ? 'ar-EG' : 'en-US', { maximumFractionDigits: 2 }).format(Number(value) || 0);
    },

    formatAmount(value) {
      return new Intl.NumberFormat(this.$store.state.locale === 'ar' ? 'ar-EG' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0);
    },

    renderChart() {
      const canvas = document.getElementById("revenueChart");
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const labels = Object.keys(this.overview.monthly_revenue);
      const values = Object.values(this.overview.monthly_revenue);

      new Chart(ctx, {
        type: "bar",
        data: {
          labels: labels,
          datasets: [
            {
              label: "Revenue (KWD)",
              data: values,
              backgroundColor: "#CBA048",
              borderRadius: 6,
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.report-navigation {
  position: relative;
  z-index: 3;
  pointer-events: auto;
}

.dashboard-export-button {
  justify-content: center;
  width: 12.75rem;
}

.summary-icon {
  display: grid;
  width: 2.6rem;
  height: 2.6rem;
  place-items: center;
  border-radius: 0.75rem;
  color: #fff;
}

.summary-icon--gold { background: #CBA048; }
.summary-icon--blue { background: #2898b4; }
.summary-icon--green { background: #29a950; }
.summary-icon--purple { background: #7964bb; }

.status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  color: #fff;
}

.status-icon--warning { background-color: #f0a433; }
.status-icon--info { background-color: #17a2b8; }
.status-icon--primary { background-color: #CBA048; }
.status-icon--success { background-color: #28a745; }
.status-icon--danger { background-color: #dc3545; }
.status-icon--secondary { background-color: #6c757d; }

.performance-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 5.25rem;
  padding: 1rem;
  border: 1px solid #f0f0f0;
  border-radius: 0.75rem;
  background: #fcfcfc;
}

.performance-card__icon {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.75rem;
  background: #fff4da;
  color: #b8862d;
}

.low-stock-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.65rem;
  border-radius: 999px;
  background: #fff0f0;
  color: #c33b3b;
  font-weight: 700;
}

.low-stock-list {
  display: grid;
  gap: 0.3rem;
}

.low-stock-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f0f0f0;
  color: inherit;
}

.low-stock-item:last-child { border-bottom: 0; }
.low-stock-item:hover { color: inherit; }

.low-stock-item__image {
  flex: 0 0 auto;
  width: 2.5rem;
  height: 2.5rem;
  object-fit: cover;
  border-radius: 0.5rem;
  background: #f8f9fa;
}

.low-stock-item__image--placeholder {
  display: grid;
  place-items: center;
  color: #8392ab;
}

.low-stock-item__image--placeholder i {
  font-size: 1.25rem;
}

.low-stock-item__details {
  display: grid;
  flex: 1 1 auto;
  min-width: 0;
}

.low-stock-item__details strong,
.low-stock-item__details span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.low-stock-item__details span {
  color: #8392ab;
  font-size: 0.75rem;
}

.low-stock-item__quantity {
  flex: 0 0 auto;
  color: #c33b3b;
  font-size: 0.8rem;
  font-weight: 700;
}

.empty-stock-state {
  display: flex;
  min-height: 10rem;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #8392ab;
  font-size: 0.875rem;
  text-align: center;
}
</style>
